/**
 * Константы мебели из конструктора: размеры кухни и модуля, углы открывания фасадов,
 * пределы размеров секций, подписи типов. Всё линейное — в миллиметрах, кроме перевода
 * в метры `METERS_PER_MM` для деталей 3D.
 */
import { KitchenModuleKind, KitchenUpperKind } from "@/api";
import type { KitchenRunId } from "./types";

/** Миллиметр в метрах: детали мебели для 3D считаются в метрах */
export const METERS_PER_MM = 0.001;

/** Углы открывания, рад: дверца на петлях, подъёмник верхнего шкафа, откидная вниз */
export const DOOR_ANGLE = (100 * Math.PI) / 180;
export const LIFT_ANGLE = (80 * Math.PI) / 180;
export const FLAP_ANGLE = (85 * Math.PI) / 180;

/** Размеры кухни, мм */
export const KITCHEN = {
	/** Глубина нижних модулей у стены и выступ столешницы */
	baseDepth: 600,
	bodyDepth: 560,
	worktopDepth: 620,
	/** Свес столешницы над фасадами нижних модулей */
	worktopNose: 42,
	plinth: 100,
	/** Верх столешницы и её толщина */
	worktopTop: 900,
	worktop: 40,
	upperDepth: 330,
	upperBottom: 1450,
	front: 18,
	board: 18,
	gap: 3,
	/** Остров: глубина со свесом для стульев и проход до основной кухни */
	islandDepth: 900,
	aisle: 1100,
	/** Модуль, к которому тянется автосборка, и пределы его ширины */
	module: 600,
	minModule: 150,
	maxModule: 900,
	/** Насколько выезжает ящик */
	drawerTravel: 400,
	/** Ширина, до которой у шкафа одна дверца; шире — две створки */
	singleDoorMax: 500,
	/** Колонна с духовкой: верх ящика, духовки и СВЧ от пола */
	columnDrawerTop: 760,
	columnOvenTop: 1360,
	columnMicrowaveTop: 1740,
	/** Холодильник: высота верхней (морозильной) дверцы и высота у новой кухни */
	fridgeTopDoor: 600,
	fridgeHeight: 1850,
	/** Над холодильником ниже верха кухни автосборка вешает шкаф, если осталось столько */
	aboveFridgeMin: 250,
} as const;

/** Пределы размеров секции кухни, мм */
export const KITCHEN_SECTION_LIMITS = {
	width: { min: 150, max: 1200 },
	/** Глубина нижней секции вместе с фасадом */
	depth: { min: 300, max: 800 },
	/** Глубина верхнего шкафа вместе с фасадом */
	upperDepth: { min: 200, max: 600 },
	/** Высота верхнего шкафа и высокой колонны */
	upperHeight: { min: 200, max: 1200 },
	tallHeight: { min: 1200, max: 3000 },
	/** Высота ряда внутри секции */
	cell: { min: 80, max: 2000 },
} as const;

/** Пределы ширины модуля, заданной вручную, мм (от 150 мм для бутылочницы до 1200 мм) */
export const KITCHEN_MODULE_WIDTH = KITCHEN_SECTION_LIMITS.width;

/** Доли высоты, где проходят швы ящиков: 1, 2, 3 или 4 ящика, нижние — глубже */
export const DRAWER_LEVELS: Record<number, number[]> = {
	1: [0, 1],
	2: [0, 0.5, 1],
	3: [0, 0.45, 0.75, 1],
	4: [0, 0.3, 0.55, 0.78, 1],
};

/** Ширина модуля техники и модуля по умолчанию, мм */
export const KITCHEN_FIXED_WIDTH: Record<KitchenModuleKind, number> = {
	[KitchenModuleKind.Doors]: KITCHEN.module,
	[KitchenModuleKind.Drawers]: KITCHEN.module,
	[KitchenModuleKind.Sink]: 600,
	[KitchenModuleKind.Cooktop]: 600,
	[KitchenModuleKind.Oven]: 600,
	[KitchenModuleKind.Bottle]: 200,
	[KitchenModuleKind.Dishwasher]: 600,
	[KitchenModuleKind.Fridge]: 600,
	[KitchenModuleKind.Column]: 600,
	[KitchenModuleKind.Empty]: 600,
};

/** Ключ верхнего шкафа автосборки: ключ нижнего модуля под ним с этим окончанием */
export const KITCHEN_UPPER_SUFFIX = "/верх";

export const KITCHEN_MODULE_LABELS: Record<KitchenModuleKind, string> = {
	[KitchenModuleKind.Doors]: "Шкаф",
	[KitchenModuleKind.Drawers]: "Ящики",
	[KitchenModuleKind.Sink]: "Мойка",
	[KitchenModuleKind.Cooktop]: "Плита",
	[KitchenModuleKind.Oven]: "Духовка снизу",
	[KitchenModuleKind.Bottle]: "Бутылочница",
	[KitchenModuleKind.Dishwasher]: "Посудомойка",
	[KitchenModuleKind.Fridge]: "Холодильник",
	[KitchenModuleKind.Column]: "Пенал с духовкой",
	[KitchenModuleKind.Empty]: "Пусто",
};

export const KITCHEN_UPPER_LABELS: Record<KitchenUpperKind, string> = {
	[KitchenUpperKind.Cabinet]: "Шкаф",
	[KitchenUpperKind.Hood]: "Вытяжка",
	[KitchenUpperKind.Empty]: "Пусто",
};

export const KITCHEN_RUN_LABELS: Record<KitchenRunId, string> = {
	back: "Задняя стена",
	left: "Левая стена",
	right: "Правая стена",
	island: "Остров",
};

/** Модуль из конструктора мебели, мм */
export const MODULE = {
	board: 18,
	back: 6,
	gap: 3,
	front: 18,
	/** Высота цоколя и ножек */
	base: 100,
	/** Высота ящика над дверями в секции «ящик и двери» */
	topDrawer: 170,
	/** Ширина секции, до которой дверца одна; шире — две створки */
	singleDoorMax: 600,
	/** Насколько выезжает ящик (не больше 3/4 глубины корпуса) */
	drawerTravel: 400,
} as const;
